import React from 'react';
import PropTypes from 'prop-types';
import OtterFigure from '../otter/OtterFigure.jsx';
import { getLevelName, MAX_LEVEL } from '../../constants/auraLevels';

/**
 * Otter-Abzeichen eines Aura-Levels.
 *
 * Seit Okt 2026 (Aura v2) zeichnet die OtterFigure den nackten Linien-Otter mit
 * der Welle seines Levels; vorher war es ein Kostuem-PNG je Level (seit Okt 2026
 * geloescht, auch im Werbespot). Die Schnittstelle fuer die Aufrufer ist gleich
 * geblieben, dazu kommt `tone` fuer die Level-Uebersicht:
 *
 * - `current` (Standard): die neueste Welle als lila Band.
 * - `reached`: schon erreicht, alle Wellen in Tinte.
 * - `locked`: noch nicht erreicht, gestrichelt in Grau.
 *
 * Gefaerbt wird nur ueber die --eo-*-Tokens der Figur (index.css), nie ueber
 * eigene Farbwerte. Keine Teile am Abzeichen: Das Level zeigt den nackten Otter.
 */
const SIZES = {
  sm: 32,
  md: 48,
  card: 72, // Level-Karte (wie im Spot)
  lg: 96,
  xl: 144,
};

const TONE_STYLES = {
  current: undefined,
  reached: { '--eo-welle': 'var(--eo-ink)' },
  locked: {
    '--eo-ink': 'rgb(var(--color-faint))',
    '--eo-welle': 'transparent',
    // Erbt auf alle Striche der Figur; Einheit = viewBox-Einheiten (1530 breit)
    strokeDasharray: '34 46',
  },
};

const clampBadgeLevel = (level) => {
  const n = Number.parseInt(level, 10);
  if (!Number.isFinite(n)) return 1;
  return Math.min(Math.max(n, 1), MAX_LEVEL);
};

const LevelBadge = ({
  level, size = 'md', showName = false, tone = 'current', className = '', imageClassName = '',
}) => {
  const lvl = clampBadgeLevel(level);
  const name = getLevelName(lvl);
  const px = SIZES[size] || SIZES.md;
  const label = tone === 'locked'
    ? `Level ${lvl}: ${name}, noch nicht erreicht`
    : `Level ${lvl}: ${name}`;

  return (
    <div className={`inline-flex flex-col items-center ${className}`.trim()} data-level-tone={tone}>
      <OtterFigure
        level={lvl}
        size={px}
        title={label}
        className={`shrink-0 ${imageClassName}`.trim()}
        style={TONE_STYLES[tone]}
      />
      {showName && (
        <span className="mt-1 text-sm font-medium text-foreground">{name}</span>
      )}
    </div>
  );
};

LevelBadge.propTypes = {
  level: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  size: PropTypes.oneOf(['sm', 'md', 'card', 'lg', 'xl']),
  showName: PropTypes.bool,
  tone: PropTypes.oneOf(['current', 'reached', 'locked']),
  className: PropTypes.string,
  imageClassName: PropTypes.string,
};

export default LevelBadge;
